import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  Box, Typography, Paper, Button, IconButton, Chip,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, Select, FormControl, InputLabel,
  ListItemText, ListItemIcon, ListItemButton,
  Collapse, Divider, Tooltip,
  Breadcrumbs, Link, Stepper, Step, StepLabel, StepContent,
  Stack, Alert, Snackbar, CircularProgress, Skeleton,
} from "@mui/material";
import { createTheme, ThemeProvider, alpha } from "@mui/material/styles";

import SearchIcon          from "@mui/icons-material/Search";
import AddIcon             from "@mui/icons-material/Add";
import EditIcon            from "@mui/icons-material/Edit";
import DeleteIcon          from "@mui/icons-material/Delete";
import PrintIcon           from "@mui/icons-material/Print";
import ContentCopyIcon     from "@mui/icons-material/ContentCopy";
import VisibilityIcon      from "@mui/icons-material/Visibility";
import ExpandLessIcon      from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon      from "@mui/icons-material/ExpandMore";
import DescriptionIcon     from "@mui/icons-material/Description";
import PolicyIcon          from "@mui/icons-material/Policy";
import AssignmentIcon      from "@mui/icons-material/Assignment";
import LocalHospitalIcon   from "@mui/icons-material/LocalHospital";
import ScienceIcon         from "@mui/icons-material/Science";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import SecurityIcon        from "@mui/icons-material/Security";
import CloseIcon           from "@mui/icons-material/Close";
import CheckCircleIcon     from "@mui/icons-material/CheckCircle";
import BookmarkIcon        from "@mui/icons-material/Bookmark";
import GroupIcon           from "@mui/icons-material/Group";
import CalendarTodayIcon   from "@mui/icons-material/CalendarToday";
import PersonIcon          from "@mui/icons-material/Person";
import ArrowBackIcon       from "@mui/icons-material/ArrowBack";
import DomainIcon          from "@mui/icons-material/Domain";
import RefreshIcon         from "@mui/icons-material/Refresh";

import { loadAllSOPDocumentsdb, loadSOPDocumentByIddb, createSOPDocumentdb, updateSOPDocumentdb, deleteSOPDocumentdb, uploadSopDocumentdb } from "../BaseURL";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import {
  AdminPageHead, ADMIN, BRAND, FONT_STACK,
  GRADIENT_GREEN, adminFieldSx, PrimaryButton, GhostButton, EmptyState,
} from "./adminUi";

// ============================================================================
//  Policy & SOP Library
// ============================================================================
//
//  ON THE LAYOUT BUG THIS FIXES
//
//    Every grid on this screen was built with MUI's `<Grid container>` +
//    `<Grid item xs={12} sm={6} md={4}>`. In MUI 7 — which is what this app
//    runs — `item` and the bare `xs`/`sm`/`md` breakpoint props are IGNORED.
//    No error, no console warning; the props simply do nothing.
//
//    That single fact explains everything that looked wrong here:
//
//      · The sidebar's four-figure stat strip (`<Grid item xs={3}>`) had no
//        column widths, so the numbers and their labels collapsed into each
//        other and rendered as "2014 6 0 / TotalSOPsPoliciesReview".
//      · The document cards (`<Grid item xs={12} sm={6} md={4}>`) all took the
//        full width, one per row, which is why the list scrolled forever.
//      · Inside the Create/Edit dialog every field went full-width for the
//        same reason, making a short form into a tall column.
//
//    All of it is CSS Grid now. See the note at the top of adminUi.js.
//
//  ON COLOUR
//
//    The screen was keyed to a teal (#1A9B7F) that appears nowhere else in the
//    product, so it read as a different application. It now takes navy + apex
//    green from brand.js like every other converted screen.
//
//    SOP and Policy keep two visibly different colours because that
//    distinction is the point of the badge — but the badge also carries the
//    WORD, so the colour is never the only thing telling them apart.

const PRIMARY      = BRAND.navy;
const PRIMARY_DARK = BRAND.navyDark;

const theme = createTheme({
  palette: {
    primary:    { main: BRAND.navy,  dark: BRAND.navyDark,  contrastText: "#fff" },
    secondary:  { main: BRAND.green, dark: BRAND.greenDark, contrastText: "#fff" },
    background: { default: BRAND.canvas, paper: BRAND.surface },
    text:       { primary: BRAND.ink, secondary: BRAND.inkMuted, disabled: BRAND.inkFaint },
    divider:    BRAND.line,
  },
  typography: { fontFamily: FONT_STACK },
  shape: { borderRadius: 10 },
  components: {
    MuiButton: { styleOverrides: { root: { textTransform: "none", borderRadius: 9, fontWeight: 600 } } },
    MuiChip:   { styleOverrides: { root: { fontWeight: 600 } } },
  },
});

// ─── sidebar tree ─────────────────────────────────────────────────────────────
const CATEGORIES = [
  { id: "governance", label: "Hospital Governance", icon: <DomainIcon />, children: [
    { id: "gov-infection", label: "Infection Control"  },
    { id: "gov-safety",    label: "Patient Safety"     },
    { id: "gov-data",      label: "Data Protection"    },
    { id: "gov-staff",     label: "Staff Welfare"      },
  ]},
  { id: "clinical", label: "Clinical SOPs", icon: <LocalHospitalIcon />, children: [
    { id: "cli-admission",  label: "Admission & Discharge" },
    { id: "cli-medication", label: "Medication"            },
    { id: "cli-surgical",   label: "Surgical Safety"       },
    { id: "cli-lab",        label: "Laboratory"            },
    { id: "cli-emergency",  label: "Emergency"             },
  ]},
  { id: "pharmacy", label: "Pharmacy Procedures", icon: <MedicalServicesIcon />, children: [
    { id: "pha-dispensing", label: "Dispensing"   },
    { id: "pha-storage",    label: "Drug Storage" },
  ]},
  { id: "lab", label: "Laboratory Protocols", icon: <ScienceIcon />, children: [
    { id: "lab-blood",   label: "Blood Collection"      },
    { id: "lab-culture", label: "Culture & Sensitivity" },
  ]},
  { id: "security", label: "Security & Privacy", icon: <SecurityIcon />, children: [
    { id: "sec-data",   label: "Data Privacy"   },
    { id: "sec-access", label: "Access Control" },
  ]},
];

function flatCategories() {
  return CATEGORIES.flatMap(cat =>
    cat.children.map(child => ({ id: child.id, label: `${cat.label} › ${child.label}` }))
  );
}

// Apex green for an SOP, brand navy for a Policy. Distinct in hue AND in
// lightness, so they stay tellable apart in greyscale, on a projector, and to
// a colourblind reader — and the badge prints the word regardless.
const TYPE_COLORS   = { SOP: BRAND.green, Policy: BRAND.navy };
const STATUS_COLORS = { Active: "success", "Under Review": "warning", Archived: "default", Draft: "info" };

const EMPTY_FORM = {
  title: "", type: "SOP", category: "cli-medication", category_label: "",
  department: "All Wards", version: "v1.0", status: "Draft",
  review_date: "", author: "", approved_by: "", description: "",
  tags: "", steps: [{ label: "", content: "" }],
};

// ─── normalise DB row ─────────────────────────────────────────────────────────
function normaliseDoc(row) {
  return {
    ...row,
    id:            Number(row.id),
    tags:          row.tags ? row.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
    steps:         row.steps || [],
    review_date:   row.review_date ? row.review_date.split("T")[0] : "",
    created_at:    row.created_at  ? row.created_at.split("T")[0]  : "",
    categoryLabel: row.category_label || row.category,
  };
}

// ─── print ────────────────────────────────────────────────────────────────────
function handlePrint(doc) {
  const win = window.open("", "_blank");
  win.document.write(`<!DOCTYPE html><html><head>
    <title>${doc.title}</title>
    <style>
      body{font-family:'Segoe UI',Arial,sans-serif;max-width:800px;margin:40px auto;color:#222}
      .header{border-bottom:3px solid #1A9B7F;padding-bottom:16px;margin-bottom:24px}
      h1{color:#1A9B7F;margin:0 0 8px}
      .meta{display:flex;gap:20px;font-size:13px;color:#555;flex-wrap:wrap}
      .badge{background:#1A9B7F;color:#fff;padding:2px 10px;border-radius:12px;font-size:12px}
      .badge-p{background:#2563eb}
      .step{margin:16px 0;padding:16px;border-left:4px solid #1A9B7F;background:#f4faf7;border-radius:4px}
      .sn{font-weight:700;color:#1A9B7F}
      .footer{margin-top:40px;font-size:11px;color:#888;border-top:1px solid #ddd;padding-top:12px}
    </style></head><body>
    <div class="header">
      <h1>${doc.title}</h1>
      <div class="meta">
        <span><b>Type:</b> <span class="badge ${doc.type === "Policy" ? "badge-p" : ""}">${doc.type}</span></span>
        <span><b>Version:</b> ${doc.version}</span>
        <span><b>Status:</b> ${doc.status}</span>
        <span><b>Department:</b> ${doc.department}</span>
        <span><b>Author:</b> ${doc.author}</span>
        <span><b>Approved by:</b> ${doc.approved_by}</span>
        <span><b>Review Date:</b> ${doc.review_date}</span>
      </div>
      <p style="margin:8px 0 0;font-style:italic;color:#444">${doc.description}</p>
    </div>
    ${(doc.steps || []).map((s, i) => `
      <div class="step">
        <div class="sn">Step ${i + 1}: ${s.label}</div>
        <p style="margin:6px 0 0">${s.content}</p>
      </div>`).join("")}
    <div class="footer">
      Printed from TotalCare Hospital Management System &bull;
      ${new Date().toLocaleDateString()} &bull; ${doc.categoryLabel}
    </div></body></html>`);
  win.document.close();
  win.print();
}

function buildCopyText(doc) {
  return [
    `TITLE: ${doc.title}`,
    `TYPE: ${doc.type}   VERSION: ${doc.version}   STATUS: ${doc.status}`,
    `DEPARTMENT: ${doc.department}`,
    `AUTHOR: ${doc.author}   APPROVED BY: ${doc.approved_by}`,
    `REVIEW DATE: ${doc.review_date}`,
    ``,
    `DESCRIPTION:\n${doc.description}`,
    ``,
    ...(doc.steps || []).map((s, i) => `STEP ${i + 1}: ${s.label}\n${s.content}`),
    ``,
    `Category: ${doc.categoryLabel}`,
    `Generated from TotalCare HIS — ${new Date().toLocaleDateString()}`,
  ].join("\n");
}

// ─── API calls ────────────────────────────────────────────────────────────────
const loadAllSOPDocuments = () =>
  axios.get(loadAllSOPDocumentsdb);

const loadSOPDocumentById = (id) =>
  axios.get(loadSOPDocumentByIddb, { params: { id } });

const createSOPDocument = (body) =>
  axios.post(createSOPDocumentdb, body);

const updateSOPDocument = (body) =>
  axios.post(updateSOPDocumentdb, body);

const deleteSOPDocument = (id) =>
  axios.post(deleteSOPDocumentdb, { id });

// ═════════════════════════════════════════════════════════════════════════════
//  COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
export default function PolicySOPManagement() {
  const [docs,         setDocs]         = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [viewDoc,      setViewDoc]      = useState(null);
  const [viewLoad,     setViewLoad]     = useState(false);
  const [editId,       setEditId]       = useState(null);   // null | "new" | number
  const [form,         setForm]         = useState(EMPTY_FORM);
  const [selectedCat,  setSelectedCat]  = useState(null);
  const [openCats,     setOpenCats]     = useState({ governance: true, clinical: true });
  const [search,       setSearch]       = useState("");
  const [filterType,   setFilterType]   = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

const [uploadState, setUploadState] = useState({
  loading:  false,
  file:     null,
  dragOver: false,
});

  const flatCats = flatCategories();

  // ── get current user from localStorage (your pattern) ─────────────────────
  const currentUser = JSON.parse(localStorage.getItem("userInfo") || "{}")?.username || "unknown";

  function toast(msg, sev = "success") { setSnack({ open: true, msg, sev }); }

  // ── load all ───────────────────────────────────────────────────────────────
  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await loadAllSOPDocuments();
      if (data.success) {
        setDocs(data.data.map(normaliseDoc));
      } else {
        toast(data.message || "Failed to load documents", "error");
      }
    } catch (error) {
      console.error("loadAllSOPDocuments error:", error);
      toast(error.response?.data?.message || error.message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  // ── open detail view ───────────────────────────────────────────────────────
  async function openView(id) {
    setViewLoad(true);
    try {
      const { data } = await loadSOPDocumentById(id);
      if (data.success) {
        setViewDoc(normaliseDoc(data.data));
      } else {
        toast(data.message || "Could not load document", "error");
      }
    } catch (error) {
      console.error("loadSOPDocumentById error:", error);
      toast(error.response?.data?.message || error.message, "error");
    } finally {
      setViewLoad(false);
    }
  }

  // ── open edit dialog ───────────────────────────────────────────────────────
  async function openEdit(docOrId) {
    let doc = typeof docOrId === "object" ? docOrId : null;

    // always fetch full doc (with steps) before editing
    try {
      const id = typeof docOrId === "object" ? docOrId.id : docOrId;
      const { data } = await loadSOPDocumentById(id);
      if (data.success) doc = normaliseDoc(data.data);
    } catch { /* fall through with partial */ }

    if (!doc) return;

    setForm({
      title:          doc.title          || "",
      type:           doc.type           || "SOP",
      category:       doc.category       || "cli-medication",
      category_label: doc.category_label || "",
      department:     doc.department     || "",
      version:        doc.version        || "v1.0",
      status:         doc.status         || "Draft",
      review_date:    doc.review_date    || "",
      author:         doc.author         || "",
      approved_by:    doc.approved_by    || "",
      description:    doc.description    || "",
      tags: Array.isArray(doc.tags) ? doc.tags.join(", ") : (doc.tags || ""),
      steps: doc.steps?.length
        ? doc.steps.map(s => ({ label: s.label, content: s.content }))
        : [{ label: "", content: "" }],
    });
    setEditId(doc.id);
  }

  function openCreate() {
  setForm({ ...EMPTY_FORM, category: selectedCat || "cli-medication" });
  setUploadState({ loading: false, file: null, dragOver: false });
  setEditId("new");
}

  // ── save ───────────────────────────────────────────────────────────────────
  async function saveDoc() {
    if (!form.title.trim()) { toast("Title is required", "error"); return; }

    const catEntry  = flatCats.find(c => c.id === form.category);
    const tagsArray = form.tags.split(",").map(t => t.trim()).filter(Boolean);

    const payload = {
      ...form,
      id:             editId === "new" ? undefined : editId,
      category_label: catEntry?.label?.split(" › ")[1] || form.category,
      tags:           tagsArray,
      steps:          form.steps.filter(s => s.label.trim()),
      created_by:     currentUser,
    };

    setSaving(true);
    try {
      const { data } = editId === "new"
        ? await createSOPDocument(payload)
        : await updateSOPDocument(payload);

      if (!data.success) { toast(data.message || "Save failed", "error"); return; }

      toast(editId === "new" ? "Document created successfully" : "Document updated successfully");
      setEditId(null);
      loadDocs();
      if (viewDoc && viewDoc.id === editId) setViewDoc(null);

    } catch (error) {
      console.error("saveDoc error:", error);
      toast(error.response?.data?.message || error.message, "error");
    } finally {
      setSaving(false);
    }
  }

  // ── delete ─────────────────────────────────────────────────────────────────
  async function confirmDelete(doc) {
    const result = await Swal.fire({
      title: "Delete Document?",
      html:  `<b>${doc.title}</b><br><small style="color:#666">This action cannot be undone.</small>`,
      icon:  "warning",
      showCancelButton:   true,
      confirmButtonColor: "#d33",
      cancelButtonColor:  "#aaa",
      confirmButtonText:  "Yes, delete it",
    });
    if (!result.isConfirmed) return;

    try {
      const { data } = await deleteSOPDocument(doc.id);
      if (!data.success) { toast(data.message || "Delete failed", "error"); return; }
      toast("Document deleted", "info");
      if (viewDoc?.id === doc.id) setViewDoc(null);
      loadDocs();
    } catch (error) {
      console.error("deleteSOPDocument error:", error);
      toast(error.response?.data?.message || error.message, "error");
    }
  }

  // ── step helpers ──────────────────────────────────────────────────────────
  function addStep()           { setForm(f => ({ ...f, steps: [...f.steps, { label: "", content: "" }] })); }
  function removeStep(i)       { setForm(f => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) })); }
  function updateStep(i, k, v) { setForm(f => { const s = [...f.steps]; s[i] = { ...s[i], [k]: v }; return { ...f, steps: s }; }); }



  // ── File upload → pre-fill form ────────────────────────────────────────────
async function handleFileUpload(file) {
  if (!file) return;
  setUploadState(s => ({ ...s, loading: true, file }));

  const formData = new FormData();
  formData.append('file', file);

  try {
    const { data } = await axios.post(uploadSopDocumentdb, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    if (!data.success) {
      toast(data.message || 'Could not parse file', 'error');
      return;
    }

    // Pre-fill form with extracted content
    setForm(f => ({
      ...f,
      title:       data.title       || f.title,
      description: data.description || f.description,
      tags:        data.tags        || f.tags,
      steps:       data.steps?.length ? data.steps : f.steps,
    }));

    toast(`✓ Extracted "${data.title}" from ${file.name}`, 'success');
  } catch (error) {
    toast(error.response?.data?.message || 'Upload failed', 'error');
  } finally {
    setUploadState(s => ({ ...s, loading: false }));
  }
}

  // ── filtered docs ──────────────────────────────────────────────────────────
  const filtered = docs.filter(d => {
    const s = search.toLowerCase();
    const matchSearch = !s ||
      d.title.toLowerCase().includes(s) ||
      (d.description || "").toLowerCase().includes(s) ||
      d.tags.some(t => t.toLowerCase().includes(s));
    const matchCat    = !selectedCat || d.category === selectedCat;
    const matchType   = filterType   === "All" || d.type   === filterType;
    const matchStatus = filterStatus === "All" || d.status === filterStatus;
    return matchSearch && matchCat && matchType && matchStatus;
  });

  const stats = {
    total:    docs.length,
    sops:     docs.filter(d => d.type   === "SOP").length,
    policies: docs.filter(d => d.type   === "Policy").length,
    review:   docs.filter(d => d.status === "Under Review").length,
  };

  // ══════════════════════════════════════════════════════════════════════════
  //  DETAIL VIEW
  // ══════════════════════════════════════════════════════════════════════════
  if (viewDoc) {
    const d = viewDoc;
    return (
      <ThemeProvider theme={theme}>
        {/* No minHeight:100vh and no background of its own. This screen renders
            INSIDE the Admin Panel, which is already inside a padded page below
            a fixed app bar — so a 100vh wrapper is taller than the space it has
            and leaves a band of dead canvas under the content. The panel
            supplies the surface; this only supplies its own inset. */}
        <Box sx={{ pb: 1 }}>

          {/* toolbar */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <IconButton onClick={() => setViewDoc(null)}><ArrowBackIcon /></IconButton>
            <Breadcrumbs sx={{ flex: 1 }}>
              <Link color="primary" sx={{ cursor: "pointer", textDecoration: "none" }} onClick={() => setViewDoc(null)}>
                Policy &amp; SOP Library
              </Link>
              <Typography color="text.primary" noWrap sx={{ maxWidth: 320 }}>{d.title}</Typography>
            </Breadcrumbs>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Copy">
                <IconButton onClick={() => { navigator.clipboard?.writeText(buildCopyText(d)); toast("Copied to clipboard"); }}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Print">
                <IconButton onClick={() => handlePrint(d)}><PrintIcon /></IconButton>
              </Tooltip>
              <Button startIcon={<EditIcon />} variant="outlined" size="small"
                onClick={() => { openEdit(d); setViewDoc(null); }}>Edit</Button>
              <Button startIcon={<DeleteIcon />} variant="outlined" color="error" size="small"
                onClick={() => confirmDelete(d)}>Delete</Button>
            </Stack>
          </Box>

          {/* A plain stack. These were `<Grid container>` + two
              `<Grid item xs={12}>`, which is a 12-of-12 span — i.e. exactly
              what a block element does on its own. Since MUI 7 ignores the
              props anyway, the Grid was doing nothing but reserving the trap
              for whoever next tried to make it two columns. */}
          <Stack spacing={2.5}>
            {/* header */}
            <Box>
              <Paper sx={{ p: 3, borderRadius: ADMIN.radius, border: `1px solid ${BRAND.line}`, borderLeft: `4px solid ${TYPE_COLORS[d.type]}` }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}>
                  <Box flex={1}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <Chip label={d.type} size="small" sx={{ bgcolor: TYPE_COLORS[d.type], color: "#fff", fontWeight: 700 }} />
                      <Chip label={d.status} size="small" color={STATUS_COLORS[d.status]} variant="outlined" />
                      <Chip label={d.version} size="small" variant="outlined" />
                    </Stack>
                    <Typography variant="h5" fontWeight={700} gutterBottom>{d.title}</Typography>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>{d.description}</Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {d.tags.map(tag => (
                        <Chip key={tag} label={`#${tag}`} size="small"
                          sx={{ bgcolor: alpha(PRIMARY, 0.1), color: PRIMARY_DARK }} />
                      ))}
                    </Stack>
                  </Box>
                  <Box sx={{ minWidth: 220 }}>
                    <Stack spacing={1}>
                      {[
                        [<PersonIcon sx={{ fontSize: 16 }} />,        "Author",      d.author],
                        [<CheckCircleIcon sx={{ fontSize: 16 }} />,   "Approved by", d.approved_by],
                        [<GroupIcon sx={{ fontSize: 16 }} />,         "Department",  d.department],
                        [<CalendarTodayIcon sx={{ fontSize: 16 }} />, "Review Date", d.review_date],
                        [<BookmarkIcon sx={{ fontSize: 16 }} />,      "Category",    d.categoryLabel],
                      ].map(([icon, label, val]) => (
                        <Stack key={label} direction="row" spacing={1} alignItems="center">
                          <Box color="text.secondary">{icon}</Box>
                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>{label}:</Typography>
                          <Typography variant="body2" fontWeight={600}>{val}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            </Box>

            {/* steps */}
            <Box>
              <Paper sx={{ p: 3, borderRadius: ADMIN.radius, border: `1px solid ${BRAND.line}` }}>
                <Typography variant="h6" sx={{ mb: 2, color: BRAND.navy, fontSize: 17, fontWeight: 800 }}>
                  {d.type === "SOP" ? "Procedure Steps" : "Policy Sections"}
                </Typography>
                {d.steps.length === 0
                  ? <Typography color="text.secondary" fontStyle="italic">No steps added yet.</Typography>
                  : (
                    <Stepper orientation="vertical" nonLinear>
                      {d.steps.map((step, i) => (
                        <Step key={i} active>
                          <StepLabel StepIconProps={{ style: { color: PRIMARY } }}>
                            <Typography fontWeight={700}>{step.label}</Typography>
                          </StepLabel>
                          <StepContent>
                            <Paper sx={{ p: 2, bgcolor: alpha(PRIMARY, 0.04), borderLeft: `3px solid ${PRIMARY}`, borderRadius: 2, mt: 0.5 }}>
                              <Typography variant="body2" sx={{ lineHeight: 1.8 }}>{step.content}</Typography>
                            </Paper>
                          </StepContent>
                        </Step>
                      ))}
                    </Stepper>
                  )}
              </Paper>
            </Box>
          </Stack>
        </Box>

        <Snackbar open={snack.open} autoHideDuration={3000}
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
        </Snackbar>
      </ThemeProvider>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  LIBRARY VIEW
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <ThemeProvider theme={theme}>
      <AdminPageHead
        icon={PolicyIcon}
        title="Policy & SOP Library"
        subtitle="Standard operating procedures and governance documents"
      />

      {/* Two panes, flowing rather than viewport-locked.
          This used to be `height: 100vh; overflow: hidden` on the row and on
          the sidebar. The screen renders INSIDE the Admin Panel, below a fixed
          app bar and inside a padded container — so a 100vh child is taller
          than the space it has, which produced a nested scrollbar and cut the
          last card in half. The sidebar sticks; the page scrolls. */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: { md: 2 }, bgcolor: "transparent" }}>

        {/* ══════════ SIDEBAR ══════════ */}
        <Box sx={{
          width: 252,
          flexShrink: 0,
          display: { xs: "none", md: "flex" },   // the tree is a desktop affordance
          flexDirection: "column",
          position: "sticky",
          top: ADMIN.stickyTop,
          maxHeight: `calc(100vh - ${ADMIN.stickyTop.sm}px - 16px)`,
          bgcolor: BRAND.surface,
          border: `1px solid ${BRAND.line}`,
          borderRadius: ADMIN.radius,
          overflow: "hidden",
        }}>

          {/* ── logo header ── */}
          <Box sx={{ px: 2, py: 1.75, background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.navyDark} 100%)`, flexShrink: 0 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ width: 34, height: 34, borderRadius: "10px", background: GRADIENT_GREEN, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <PolicyIcon sx={{ color: "#fff", fontSize: 19 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography fontSize={10.5} sx={{ color: "rgba(255,255,255,.65)" }} fontWeight={600} lineHeight={1.2}>TotalCare HIS</Typography>
                <Typography fontSize={13} color="#fff" fontWeight={700} lineHeight={1.3}>Policy &amp; SOP Library</Typography>
              </Box>
            </Stack>
          </Box>

          {/* ── stats strip ──
              A real 2×2 CSS grid. `<Grid item xs={3}>` gave these four figures
              no width at all in MUI 7, so they ran together into the
              "2014 6 0 / TotalSOPsPoliciesReview" mess. 2×2 rather than 1×4
              because "Policies" does not fit in a quarter of 252px without
              shrinking the label below readable size. */}
          <Box sx={{
            p: 1.25, flexShrink: 0,
            borderBottom: `1px solid ${BRAND.line}`,
            background: BRAND.canvas,
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: .75,
          }}>
            {[
              ["Total",    stats.total,    BRAND.navy],
              ["SOPs",     stats.sops,     BRAND.green],
              ["Policies", stats.policies, BRAND.navy],
              ["Review",   stats.review,   BRAND.warning],
            ].map(([l, v, c]) => (
              <Box key={l} sx={{
                bgcolor: BRAND.surface,
                border: `1px solid ${BRAND.line}`,
                borderRadius: "8px",
                px: 1, py: .85,
                minWidth: 0,
              }}>
                {loading
                  ? <Skeleton variant="text" width={22} />
                  : <Typography fontWeight={800} fontSize={17} sx={{ color: c }} lineHeight={1.05}>{v}</Typography>}
                <Typography noWrap fontSize={10} sx={{ color: BRAND.inkMuted, fontWeight: 600, letterSpacing: ".2px" }}>{l}</Typography>
              </Box>
            ))}
          </Box>

          {/* ── nav (scrollable) — All Docs + category tree in ONE container ── */}
          <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>

            {/* All Documents */}
            <ListItemButton
              selected={!selectedCat}
              onClick={() => setSelectedCat(null)}
              sx={{
                mx: 1, mb: 0.5, borderRadius: 2, minHeight: 40,
                "&.Mui-selected": { bgcolor: alpha(PRIMARY, 0.1), color: PRIMARY },
                "&.Mui-selected:hover": { bgcolor: alpha(PRIMARY, 0.15) },
              }}>
              <ListItemIcon sx={{ minWidth: 30 }}>
                <AssignmentIcon sx={{ fontSize: 18, color: !selectedCat ? PRIMARY : "text.secondary" }} />
              </ListItemIcon>
              <ListItemText
                primary="All Documents"
                primaryTypographyProps={{ fontSize: 13, fontWeight: !selectedCat ? 700 : 500 }}
              />
              <Chip
                label={docs.length}
                size="small"
                sx={{ height: 18, fontSize: 10, fontWeight: 700,
                  bgcolor: !selectedCat ? PRIMARY : alpha(PRIMARY, 0.1),
                  color: !selectedCat ? "#fff" : PRIMARY }}
              />
            </ListItemButton>

            <Divider sx={{ mx: 2, my: 1 }} />

            {/* Category groups */}
            {CATEGORIES.map((cat) => {
              const catCount = docs.filter(d => cat.children.some(c => c.id === d.category)).length;
              return (
                <Box key={cat.id}>
                  {/* group header */}
                  <ListItemButton
                    onClick={() => setOpenCats(p => ({ ...p, [cat.id]: !p[cat.id] }))}
                    sx={{ mx: 1, borderRadius: 2, minHeight: 36, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      {React.cloneElement(cat.icon, { sx: { fontSize: 16, color: PRIMARY } })}
                    </ListItemIcon>
                    <ListItemText
                      primary={cat.label}
                      primaryTypographyProps={{ fontSize: 11.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}
                    />
                    {catCount > 0 && (
                      <Typography fontSize={10} color="text.disabled" mr={0.5}>{catCount}</Typography>
                    )}
                    {openCats[cat.id]
                      ? <ExpandLessIcon sx={{ fontSize: 15, color: "text.disabled" }} />
                      : <ExpandMoreIcon sx={{ fontSize: 15, color: "text.disabled" }} />}
                  </ListItemButton>

                  {/* child items */}
                  <Collapse in={openCats[cat.id]}>
                    <Box sx={{ mb: 0.5 }}>
                      {cat.children.map((child) => {
                        const count = docs.filter(d => d.category === child.id).length;
                        const isSelected = selectedCat === child.id;
                        return (
                          <ListItemButton
                            key={child.id}
                            selected={isSelected}
                            onClick={() => setSelectedCat(child.id)}
                            sx={{
                              pl: 4.5, mx: 1, py: 0.4, borderRadius: 1.5, minHeight: 32,
                              "&.Mui-selected": { bgcolor: alpha(PRIMARY, 0.1), color: PRIMARY },
                              "&.Mui-selected:hover": { bgcolor: alpha(PRIMARY, 0.15) },
                            }}>
                            <ListItemText
                              primary={child.label}
                              primaryTypographyProps={{ fontSize: 12.5, fontWeight: isSelected ? 700 : 400, color: isSelected ? PRIMARY : "text.primary" }}
                            />
                            {count > 0 && (
                              <Typography fontSize={11} color={isSelected ? PRIMARY : "text.disabled"} fontWeight={isSelected ? 700 : 400}>
                                {count}
                              </Typography>
                            )}
                          </ListItemButton>
                        );
                      })}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* ══════════ MAIN CONTENT ══════════ */}
        <Box sx={{
          flex: 1, minWidth: 0,
          display: "flex", flexDirection: "column",
          bgcolor: BRAND.surface,
          border: `1px solid ${BRAND.line}`,
          borderRadius: ADMIN.radius,
          overflow: "hidden",
        }}>

          {/* ── topbar ──
              Wraps instead of clipping. It was a fixed 60px row of six
              controls with no wrap, so on anything narrower than about
              1100px the New Document button was pushed off the edge. */}
          <Box sx={{
            px: { xs: 1.75, sm: 2.5 }, py: 1.5,
            bgcolor: BRAND.surface,
            borderBottom: `1px solid ${BRAND.line}`,
            display: "flex", alignItems: "center", gap: 1.25,
            flexWrap: "wrap",
            flexShrink: 0,
          }}>
            {/* search */}
            <TextField
              size="small"
              placeholder="Search documents, tags…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ ...adminFieldSx, width: { xs: "100%", sm: 240 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* type filter */}
            <FormControl size="small" sx={{ ...adminFieldSx, width: 132 }}>
              <InputLabel shrink>Type</InputLabel>
              <Select label="Type" notched value={filterType} onChange={e => setFilterType(e.target.value)}>
                <MenuItem value="All">All Types</MenuItem>
                <MenuItem value="SOP">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: TYPE_COLORS.SOP }} />
                    <span>SOP</span>
                  </Stack>
                </MenuItem>
                <MenuItem value="Policy">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: TYPE_COLORS.Policy }} />
                    <span>Policy</span>
                  </Stack>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Category filter — mobile only.
                The category tree lives in the sidebar, and the sidebar is a
                desktop affordance: a six-group expandable tree on a 360px
                screen pushes the documents themselves a full screen down. But
                hiding it must not remove the CAPABILITY, so the same choice is
                offered here as a flat select below md. */}
            <FormControl size="small" sx={{ ...adminFieldSx, display: { xs: "flex", md: "none" }, minWidth: 170, flex: { xs: "1 1 170px", sm: "0 0 auto" } }}>
              <InputLabel shrink>Category</InputLabel>
              <Select
                label="Category" notched
                value={selectedCat || "All"}
                onChange={e => setSelectedCat(e.target.value === "All" ? null : e.target.value)}
              >
                <MenuItem value="All">All Categories</MenuItem>
                {flatCats.map(c => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}
              </Select>
            </FormControl>

            {/* status filter */}
            <FormControl size="small" sx={{ ...adminFieldSx, width: 150 }}>
              <InputLabel shrink>Status</InputLabel>
              <Select label="Status" notched value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <MenuItem value="All">All Status</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Under Review">Under Review</MenuItem>
                <MenuItem value="Draft">Draft</MenuItem>
                <MenuItem value="Archived">Archived</MenuItem>
              </Select>
            </FormControl>

            {/* refresh */}
            <Tooltip title="Refresh list">
              <span>
                <IconButton onClick={loadDocs} disabled={loading} size="small">
                  {loading ? <CircularProgress size={16} sx={{ color: PRIMARY }} /> : <RefreshIcon sx={{ fontSize: 20 }} />}
                </IconButton>
              </span>
            </Tooltip>

            {/* spacer */}
            <Box flex={1} />

            {/* result count */}
            {!loading && (
              <Typography variant="body2" color="text.secondary" fontSize={12}>
                {filtered.length} document{filtered.length !== 1 ? "s" : ""}
                {selectedCat && <> &nbsp;·&nbsp; filtered</>}
              </Typography>
            )}

            {/* new document */}
            <PrimaryButton startIcon={<AddIcon />} onClick={openCreate} sx={{ py: .8, height: 36 }}>
              New Document
            </PrimaryButton>
          </Box>

          {/* ── active filter breadcrumb ── */}
          {(selectedCat || filterType !== "All" || filterStatus !== "All" || search) && (
            <Box sx={{ px: 3, py: 0.75, bgcolor: alpha(PRIMARY, 0.04), borderBottom: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
              <Typography fontSize={12} color="text.secondary">Showing:</Typography>
              {selectedCat && (
                <Chip size="small" label={flatCats.find(c => c.id === selectedCat)?.label || selectedCat}
                  onDelete={() => setSelectedCat(null)}
                  sx={{ height: 20, fontSize: 11, bgcolor: alpha(PRIMARY, 0.1), color: PRIMARY_DARK }} />
              )}
              {filterType !== "All" && (
                <Chip size="small" label={`Type: ${filterType}`} onDelete={() => setFilterType("All")}
                  sx={{ height: 20, fontSize: 11 }} />
              )}
              {filterStatus !== "All" && (
                <Chip size="small" label={`Status: ${filterStatus}`} onDelete={() => setFilterStatus("All")}
                  sx={{ height: 20, fontSize: 11 }} />
              )}
              {search && (
                <Chip size="small" label={`"${search}"`} onDelete={() => setSearch("")}
                  sx={{ height: 20, fontSize: 11 }} />
              )}
              <Box flex={1} />
              <Button size="small" sx={{ color: "text.secondary", fontSize: 11, py: 0 }}
                onClick={() => { setSearch(""); setFilterType("All"); setFilterStatus("All"); setSelectedCat(null); }}>
                Clear all
              </Button>
            </Box>
          )}

          {/* detail loading overlay */}
          {viewLoad && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, py: 2, bgcolor: alpha(PRIMARY, 0.04), flexShrink: 0 }}>
              <CircularProgress size={18} sx={{ color: PRIMARY }} />
              <Typography fontSize={13} color="text.secondary">Loading document…</Typography>
            </Box>
          )}

          {/* ── document card grid ──
              TWO per row from sm up, one on a phone. Was
              `<Grid item xs={12} sm={6} md={4}>` — props MUI 7 ignores — so
              every card took the full width and the library became an endless
              single column.

              `alignItems: start` keeps each card its own height instead of
              stretching a two-line card to match a four-line neighbour. */}
          <Box sx={{
            p: { xs: 1.5, sm: 2 },
            display: "grid",
            gap: { xs: 1.5, sm: 1.75 },
            alignItems: "start",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: "repeat(2, minmax(0, 1fr))",
            },
          }}>
            {loading
              ? [1, 2, 3, 4].map(n => (
                  <Skeleton key={n} variant="rounded" height={168} sx={{ borderRadius: "12px" }} />
                ))
              : filtered.map((doc) => (
                  <Box
                    key={doc.id}
                    sx={{
                      minWidth: 0,
                      display: "flex", flexDirection: "column",
                      bgcolor: BRAND.surface,
                      borderRadius: "12px",
                      border: `1px solid ${BRAND.line}`,
                      // The type colour as a left edge rather than a full bar
                      // across the top: same signal, four fewer pixels of
                      // card height, and it survives being read at a glance
                      // down a two-column list.
                      borderLeft: `3px solid ${TYPE_COLORS[doc.type]}`,
                      overflow: "hidden",
                      transition: "box-shadow .18s ease, border-color .18s ease",
                      "&:hover": {
                        boxShadow: ADMIN.shadowLift,
                        borderColor: BRAND.navyLight,
                        borderLeftColor: TYPE_COLORS[doc.type],
                      },
                    }}
                  >
                    {/* header: badges, title, meta — one block, no divider */}
                    <Box sx={{ px: 1.75, pt: 1.5, pb: 1.25 }}>
                      <Stack direction="row" spacing={.75} alignItems="center" sx={{ mb: 1 }}>
                        <Chip
                          label={doc.type}
                          size="small"
                          sx={{ bgcolor: TYPE_COLORS[doc.type], color: "#fff", fontWeight: 700, fontSize: 10, height: 19 }}
                        />
                        <Chip
                          label={doc.status}
                          size="small"
                          color={STATUS_COLORS[doc.status]}
                          variant="outlined"
                          sx={{ fontSize: 9.5, height: 19 }}
                        />
                        <Box sx={{ flex: 1 }} />
                        <Typography noWrap fontSize={10.5} sx={{ color: BRAND.inkFaint, fontWeight: 600 }}>
                          {doc.version}
                        </Typography>
                      </Stack>

                      <Typography
                        title={doc.title}
                        sx={{
                          fontSize: 13.5, fontWeight: 700, color: BRAND.ink,
                          lineHeight: 1.35, mb: .3,
                          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                        }}
                      >
                        {doc.title}
                      </Typography>

                      <Typography noWrap fontSize={11} sx={{ color: BRAND.inkFaint }}>
                        {doc.categoryLabel}
                      </Typography>

                      {/* One line of description, not two. In a two-column
                          grid the summary is a hint that you have the right
                          document — the full text is one click away. */}
                      <Typography
                        sx={{
                          mt: .9, fontSize: 11.5, color: BRAND.inkMuted, lineHeight: 1.55,
                          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                        }}
                      >
                        {doc.description || "No description provided."}
                      </Typography>

                      {doc.tags.length > 0 && (
                        <Stack direction="row" flexWrap="wrap" gap={.5} mt={1}>
                          {doc.tags.slice(0, 3).map(t => (
                            <Chip key={t} label={t} size="small"
                              sx={{ fontSize: 9.5, height: 17, bgcolor: BRAND.navySoft, color: BRAND.navy, fontWeight: 600 }} />
                          ))}
                          {doc.tags.length > 3 && (
                            <Chip label={`+${doc.tags.length - 3}`} size="small"
                              sx={{ fontSize: 9.5, height: 17, bgcolor: BRAND.canvas, color: BRAND.inkMuted }} />
                          )}
                        </Stack>
                      )}
                    </Box>

                    {/* actions */}
                    <Box sx={{
                      mt: "auto",
                      px: 1.25, py: .75,
                      borderTop: `1px solid ${BRAND.line}`,
                      background: BRAND.canvas,
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}>
                      <Stack direction="row" spacing={0}>
                        <Tooltip title="Print" arrow>
                          <IconButton size="small" onClick={() => handlePrint(doc)} sx={{ color: BRAND.inkMuted }}>
                            <PrintIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copy text" arrow>
                          <IconButton size="small" onClick={() => { navigator.clipboard?.writeText(buildCopyText(doc)); toast("Copied!"); }} sx={{ color: BRAND.inkMuted }}>
                            <ContentCopyIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit" arrow>
                          <IconButton size="small" onClick={() => openEdit(doc)} sx={{ color: BRAND.inkMuted }}>
                            <EditIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete" arrow>
                          <IconButton size="small" onClick={() => confirmDelete(doc)} sx={{ color: BRAND.danger }}>
                            <DeleteIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => openView(doc.id)}
                        startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                        sx={{
                          fontSize: 11.5, fontWeight: 700, px: 1.25, height: 28,
                          color: BRAND.navy,
                          "&:hover": { bgcolor: BRAND.navySoft },
                        }}
                      >
                        View
                      </Button>
                    </Box>
                  </Box>
                ))}

            {/* Spans both columns so it sits centred under the toolbar rather
                than squeezed into the left-hand cell. */}
            {!loading && filtered.length === 0 && (
              <Box sx={{ gridColumn: "1 / -1" }}>
                <EmptyState
                  icon={DescriptionIcon}
                  title="No documents found"
                  body="Nothing matches the current search and filters."
                  action={
                    <GhostButton
                      onClick={() => { setSearch(""); setFilterType("All"); setFilterStatus("All"); setSelectedCat(null); }}
                    >
                      Clear filters
                    </GhostButton>
                  }
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* ══════════ Create / Edit Dialog ══════════ */}
      {/* fullWidth + maxWidth="md" is not cosmetic. The dialog had neither, so
          it fell back to MUI's default `sm` (600px) — and with its inner
          `<Grid item xs={6} sm={3}>` columns being ignored by MUI 7, every
          field stacked into one narrow column. A six-field form became a
          scroll. It is now a real two/four-column grid in a wider sheet. */}
      <Dialog
        open={!!editId}
        fullWidth
        maxWidth="md"
        onClose={() => { if (!saving) { setEditId(null); setUploadState({ loading: false, file: null, dragOver: false }); } }}
        PaperProps={{ sx: { borderRadius: ADMIN.radius, overflow: "hidden", fontFamily: FONT_STACK } }}>

        {/* ── title bar ── */}
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{ background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.navyDark} 100%)`, px: { xs: 2, sm: 3 }, py: 2.25 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
              <Stack direction="row" spacing={1.75} alignItems="center" sx={{ minWidth: 0 }}>
                <Box sx={{
                  width: 38, height: 38, borderRadius: "11px", flexShrink: 0,
                  background: GRADIENT_GREEN, display: "grid", placeItems: "center",
                }}>
                  {editId === "new"
                    ? <AddIcon sx={{ color: "#fff", fontSize: 20 }} />
                    : <EditIcon sx={{ color: "#fff", fontSize: 19 }} />}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontWeight={800} fontSize={16.5} color="#fff" lineHeight={1.25}>
                    {editId === "new" ? "Create New Document" : "Edit Document"}
                  </Typography>
                  <Typography fontSize={12} sx={{ color: "rgba(255,255,255,.75)" }} mt={0.2}>
                    Import a file to fill this in automatically, or complete it by hand
                  </Typography>
                </Box>
              </Stack>
              <IconButton size="small" onClick={() => !saving && setEditId(null)}
                sx={{ color: "#fff", flexShrink: 0, bgcolor: "rgba(255,255,255,.12)", "&:hover": { bgcolor: "rgba(255,255,255,.24)" } }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, bgcolor: BRAND.canvas }}>


          {/* ── File Upload Zone ── */}
<Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 0 }}>
  <Stack direction="row" alignItems="center" spacing={1} mb={1.25}>
    <Box sx={{ width: 3, height: 16, bgcolor: BRAND.green, borderRadius: 1 }} />
    <Typography fontWeight={800} fontSize={11} sx={{ color: BRAND.inkFaint }}
      textTransform="uppercase" letterSpacing={1}>
      Import from file <Typography component="span" fontSize={11} fontWeight={500}
        sx={{ color: BRAND.inkFaint }} textTransform="none" letterSpacing={0}>— optional, auto-fills the form</Typography>
    </Typography>
  </Stack>

  <Box
    onDragOver={e => { e.preventDefault(); setUploadState(s => ({ ...s, dragOver: true })); }}
    onDragLeave={() => setUploadState(s => ({ ...s, dragOver: false }))}
    onDrop={e => {
      e.preventDefault();
      setUploadState(s => ({ ...s, dragOver: false }));
      const file = e.dataTransfer.files[0];
      if (file) handleFileUpload(file);
    }}
    onClick={() => document.getElementById('sop-file-input').click()}
    sx={{
      border: '1.5px dashed',
      borderColor: uploadState.dragOver ? BRAND.green : uploadState.file ? BRAND.green : BRAND.line,
      borderRadius: ADMIN.radiusSm,
      p: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      cursor: 'pointer',
      bgcolor: uploadState.dragOver || uploadState.file ? BRAND.greenSoft : BRAND.surface,
      transition: 'border-color .18s ease, background-color .18s ease',
      '&:hover': { borderColor: BRAND.green, bgcolor: BRAND.greenSoft }
    }}
  >
    <input
      id="sop-file-input"
      type="file"
      accept=".docx,.doc,.txt"
      style={{ display: 'none' }}
      onChange={e => { const f = e.target.files[0]; if (f) handleFileUpload(f); e.target.value = ''; }}
    />

    {uploadState.loading ? (
      <>
        <CircularProgress size={28} sx={{ color: PRIMARY, flexShrink: 0 }} />
        <Box>
          <Typography fontWeight={600} fontSize={13} color={PRIMARY}>
            Extracting content…
          </Typography>
          <Typography fontSize={11} color="text.secondary">
            Reading document structure and sections
          </Typography>
        </Box>
      </>
    ) : uploadState.file ? (
      <>
        <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: alpha(PRIMARY, 0.12),
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <InsertDriveFileIcon sx={{ color: PRIMARY, fontSize: 22 }} />
        </Box>
        <Box flex={1}>
          <Typography fontWeight={700} fontSize={13} color={PRIMARY}>
            {uploadState.file.name}
          </Typography>
          <Typography fontSize={11} color="text.secondary">
            {(uploadState.file.size / 1024).toFixed(1)} KB — form has been pre-filled. You can edit below.
          </Typography>
        </Box>
        <Chip label="Re-upload" size="small" variant="outlined"
          sx={{ fontSize: 10, color: 'text.secondary', borderColor: 'divider' }}
          onClick={e => { e.stopPropagation(); document.getElementById('sop-file-input').click(); }} />
      </>
    ) : (
      <>
        <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: alpha('#0ea5e9', 0.1),
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <UploadFileIcon sx={{ color: '#0ea5e9', fontSize: 22 }} />
        </Box>
        <Box>
          <Typography fontWeight={600} fontSize={13} color="text.primary">
            Drop a .docx or .txt file here, or click to browse
          </Typography>
          <Typography fontSize={11} color="text.secondary">
            Title, description, and procedure steps will be extracted automatically
          </Typography>
        </Box>
      </>
    )}
  </Box>
</Box>

{/* Divider between upload and identity */}
<Box sx={{ px: 3, pt: 2 }}>
  <Divider>
    <Typography fontSize={11} color="text.disabled" px={1}>or fill in manually</Typography>
  </Divider>
</Box>

          {/* ── Section 1: Document Identity ── */}
          <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1.75}>
              <Box sx={{ width: 3, height: 16, bgcolor: BRAND.navy, borderRadius: 1 }} />
              <Typography fontWeight={800} fontSize={11} sx={{ color: BRAND.inkFaint }} textTransform="uppercase" letterSpacing={1}>
                Document identity
              </Typography>
            </Stack>

            {/* Row 1: Title (full width) */}
            <TextField
              fullWidth
              size="small"
              label="Document Title *"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              sx={{ ...adminFieldSx, mb: 2 }}
              placeholder="e.g. Medication Administration SOP"
            />

            {/* Row 2: Type | Status | Version | Review date.
                Real CSS Grid: 2 columns on a phone, 4 from sm. The
                `<Grid item xs={6} sm={3}>` this replaces did nothing at all in
                MUI 7, so these four short fields each took a full row. */}
            <Box sx={{
              display: "grid", gap: 1.75,
              gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))" },
            }}>
              <FormControl fullWidth size="small" sx={adminFieldSx}>
                <InputLabel shrink>Type *</InputLabel>
                <Select label="Type *" notched value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                  <MenuItem value="SOP">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: TYPE_COLORS.SOP }} />
                      <span>SOP</span>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="Policy">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: TYPE_COLORS.Policy }} />
                      <span>Policy</span>
                    </Stack>
                  </MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={adminFieldSx}>
                <InputLabel shrink>Status</InputLabel>
                <Select label="Status" notched value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <MenuItem value="Draft">Draft</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Under Review">Under Review</MenuItem>
                  <MenuItem value="Archived">Archived</MenuItem>
                </Select>
              </FormControl>

              <TextField fullWidth size="small" label="Version" value={form.version} sx={adminFieldSx}
                onChange={e => setForm(f => ({ ...f, version: e.target.value }))}
                placeholder="v1.0" />

              <TextField fullWidth size="small" type="date" label="Review Date" value={form.review_date}
                onChange={e => setForm(f => ({ ...f, review_date: e.target.value }))}
                InputLabelProps={{ shrink: true }} sx={adminFieldSx} />
            </Box>
          </Box>

          <Divider />

          {/* ── Section 2: Classification ── */}
          <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.25 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1.75}>
              <Box sx={{ width: 3, height: 16, bgcolor: BRAND.navy, borderRadius: 1 }} />
              <Typography fontWeight={800} fontSize={11} sx={{ color: BRAND.inkFaint }} textTransform="uppercase" letterSpacing={1}>
                Classification &amp; ownership
              </Typography>
            </Stack>

            <Box sx={{
              display: "grid", gap: 1.75,
              gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
            }}>
              <FormControl fullWidth size="small" sx={adminFieldSx}>
                <InputLabel shrink>Category *</InputLabel>
                <Select label="Category *" notched value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  {flatCats.map(c => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}
                </Select>
              </FormControl>

              <TextField fullWidth size="small" label="Department / Area" value={form.department} sx={adminFieldSx}
                onChange={e => setForm(f => ({ ...f, department: e.target.value }))}
                placeholder="e.g. All Wards, A&E, Theatre" />

              <TextField fullWidth size="small" label="Author / Prepared By" value={form.author} sx={adminFieldSx}
                onChange={e => setForm(f => ({ ...f, author: e.target.value }))}
                placeholder="e.g. Dr. Mensah" />

              <TextField fullWidth size="small" label="Approved By" value={form.approved_by} sx={adminFieldSx}
                onChange={e => setForm(f => ({ ...f, approved_by: e.target.value }))}
                placeholder="e.g. Medical Director" />
            </Box>
          </Box>

          <Divider />

          {/* ── Section 3: Description & Tags ── */}
          <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.25 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1.75}>
              <Box sx={{ width: 3, height: 16, bgcolor: BRAND.navy, borderRadius: 1 }} />
              <Typography fontWeight={800} fontSize={11} sx={{ color: BRAND.inkFaint }} textTransform="uppercase" letterSpacing={1}>
                Description &amp; tags
              </Typography>
            </Stack>

            <TextField fullWidth size="small" multiline rows={3} label="Description / Purpose" value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Briefly describe what this document covers and why it exists"
              sx={{ ...adminFieldSx, mb: 2 }} />

            <TextField fullWidth size="small" label="Tags" value={form.tags}
              onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
              placeholder="e.g. medication, safety, nurses, pharmacy"
              helperText="Separate tags with commas — used for search and filtering"
              sx={adminFieldSx} />
          </Box>

          <Divider />

          {/* ── Section 4: Steps ── */}
          <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.25 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.75} spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ width: 3, height: 16, bgcolor: BRAND.green, borderRadius: 1 }} />
                <Typography fontWeight={800} fontSize={11} sx={{ color: BRAND.inkFaint }} textTransform="uppercase" letterSpacing={1}>
                  {form.type === "SOP" ? "Procedure steps" : "Policy sections"}
                </Typography>
                <Chip label={form.steps.length} size="small"
                  sx={{ bgcolor: BRAND.greenSoft, color: BRAND.greenDark, fontWeight: 700, height: 19, fontSize: 10.5 }} />
              </Stack>
              <GhostButton size="small" startIcon={<AddIcon />} onClick={addStep} sx={{ py: .5, px: 1.5, fontSize: 12 }}>
                Add step
              </GhostButton>
            </Stack>

            <Stack spacing={1.5}>
              {form.steps.map((step, i) => (
                <Paper key={i} elevation={0}
                  sx={{ borderRadius: 2.5, border: "1px solid", borderColor: alpha("#7c3aed", 0.2), overflow: "hidden" }}>
                  {/* step header */}
                  <Box sx={{ px: 2, py: 1, bgcolor: alpha("#7c3aed", 0.06), borderBottom: "1px solid", borderColor: alpha("#7c3aed", 0.12) }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 22, height: 22, borderRadius: "50%", bgcolor: "#7c3aed", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Typography fontSize={11} fontWeight={700} color="#fff">{i + 1}</Typography>
                        </Box>
                        <Typography fontSize={12} fontWeight={700} color="#5b21b6">
                          Step {i + 1}
                        </Typography>
                      </Stack>
                      {form.steps.length > 1 && (
                        <IconButton size="small" onClick={() => removeStep(i)}
                          sx={{ color: "#ef4444", bgcolor: alpha("#ef4444", 0.08), "&:hover": { bgcolor: alpha("#ef4444", 0.15) }, width: 24, height: 24 }}>
                          <CloseIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      )}
                    </Stack>
                  </Box>
                  {/* step body */}
                  <Box sx={{ p: 2, bgcolor: "#fff" }}>
                    <TextField fullWidth size="small" label="Step Title"
                      value={step.label} onChange={e => updateStep(i, "label", e.target.value)}
                      sx={{ mb: 1.5 }} placeholder="e.g. Verify Doctor's Order" />
                    <TextField fullWidth size="small" multiline rows={3} label="Detailed Instructions"
                      value={step.content} onChange={e => updateStep(i, "content", e.target.value)}
                      placeholder="Describe exactly what should be done in this step…" />
                  </Box>
                </Paper>
              ))}
            </Stack>

            {/* add step hint */}
            <Button fullWidth onClick={addStep} startIcon={<AddIcon />}
              sx={{ mt: 2, py: 1.2, border: "2px dashed", borderColor: alpha(PRIMARY, 0.3), color: PRIMARY, borderRadius: 2,
                "&:hover": { borderColor: PRIMARY, bgcolor: alpha(PRIMARY, 0.04) }, fontWeight: 600, fontSize: 13 }}>
              + Add Another Step
            </Button>
          </Box>

        </DialogContent>

        {/* ── footer ── */}
        <DialogActions sx={{ px: 3, py: 2, bgcolor: "#fff", borderTop: "1px solid", borderColor: "divider", gap: 1 }}>
          <Button onClick={() => setEditId(null)} disabled={saving}
            sx={{ color: "text.secondary", minWidth: 100 }}>
            Cancel
          </Button>
          <Box flex={1} />
          <Button variant="contained" onClick={saveDoc} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
            sx={{ bgcolor: PRIMARY, "&:hover": { bgcolor: PRIMARY_DARK }, minWidth: 160, py: 1.1, fontSize: 14, fontWeight: 700 }}>
            {saving ? "Saving…" : editId === "new" ? "Create Document" : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══════════ Snackbar ══════════ */}
      <Snackbar open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </ThemeProvider>
  );
}